// Wat de aangemelde gebruiker met een taak mag, zoals de taaklade het altijd al uitrekende; de taakpagina (scherm
// Taken) gebruikt dezelfde regels. Rechten komen uit de rol (Entra) en Instellingen › Rollen en rechten; schrijven
// vanuit de app kan enkel met een Project-licentie (me.canWrite), anders gebeurt het in Planner zelf.
import { useMe, useProposals } from "../../lib/hooks";
import { plannerTaskUrl } from "../../lib/planning";
import { rightsOf, useSettings } from "../../lib/settings";
import type { Me, Proposal, Task } from "../../lib/types";

export interface TaskPerm {
  me: Me;
  med: boolean;
  /** de gebruiker is eigenaar van de taak */
  own: boolean;
  /** mag de taak wijzigen volgens rol en eigenaarschap, los van de licentie */
  canEdit: boolean;
  /** een medewerker mag deze taak zonder eigenaar zelf oppakken */
  canPick: boolean;
  readOnly: boolean;
  /** Project-licentie: de app mag namens de gebruiker naar Planner schrijven */
  write: boolean;
  /** wijzigen vanuit de app: canEdit met licentie */
  edit: boolean;
  /** wat enkel de planner doet: eigenaars, afhankelijkheden, bucket, fase, mijlpaal */
  plan: boolean;
  /** mag het wel, maar in Planner zelf (geen licentie) */
  inPlanner: boolean;
  /** een medewerker die de deadline zelf zet (Instellingen) */
  medDirect: boolean;
  /** een medewerker mag de taak van een collega afwerken (Instellingen) */
  finishOther: boolean;
  /** start en deadline rechtstreeks kiezen */
  direct: boolean;
  /** een nieuwe deadline voorstellen aan de planner */
  proposeEnd: boolean;
  /** het voorstel dat op de planner wacht, als er een is */
  proposal: Proposal | null;
  /** de taak in Planner zelf */
  url: string;
  rights: ReturnType<typeof rightsOf>;
}

export function useTaskPerm(task: Task): TaskPerm | null {
  const { data: me } = useMe();
  const { data: proposals } = useProposals();
  const rights = rightsOf(useSettings().data?.settings);
  if (!me) return null;
  const med = me.role === "medewerker";
  const own = task.owners.some((o) => o.personId === me.id);
  const canEdit = !med || own;
  const canPick = med && task.owners.length === 0 && rights.pickUnowned;
  const write = me.canWrite;
  const edit = canEdit && write;
  const plan = !med && write;
  const medDirect = med && rights.deadline === "direct" && edit;
  const proposal = (proposals || []).find((p) => p.taskId === task.id) || null;
  const direct = plan || medDirect;
  return {
    me, med, own, canEdit, canPick, readOnly: !canEdit, write, edit, plan,
    inPlanner: !write && (canEdit || canPick), medDirect,
    finishOther: med && !own && task.owners.length > 0 && rights.finishOthers && write,
    direct, proposeEnd: med && !medDirect && own && rights.deadline !== "niet" && !proposal,
    proposal, url: plannerTaskUrl(me, task), rights,
  };
}

/** "Jens", "Jens en Arthur", "Jens, Lennert en Arthur" */
export const firstNames = (names: string[]) => (names.length < 2 ? names.join("") : names.slice(0, -1).join(", ") + " en " + names[names.length - 1]);

/** Een uuid voor een nieuw checklistpunt: de app kiest ze zelf, zodat Ongedaan maken het punt kan weghalen. */
export function newId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => { const r = (Math.random() * 16) | 0; return (c === "x" ? r : (r & 3) | 8).toString(16); });
}
